import { FINANCIAL_FORECAST_REDUCER_KEY } from "../index";
import { getUnitSymbolByUnit, itemType, unitType } from "../types";
import { years } from "store/utils/years";
import { defaultZeroMonths } from "./staticMonthsData";
import { GeneralCoType } from "constants/companyTypes";
import * as actionTypes from "../../actions/actionTypes";

export const RECURRING_CHARGES_REDUCER_KEY = "recurringCharges";
export const CUSTOMERS_AT_THE_START_KEY = "customersAtTheStart";
export const SIGNUPS_KEY = "signups";
export const RENEWABLES_KEY = "renewables";
export const CHURN_RATE_KEY = "churnRate";
export const CUSTOMERS_AT_THE_END_KEY = "customersAtTheEnd";
export const RECURRING_CHARGE_KEY = "recurringCharge";
export const INCOME_FROM_RECURRING_CHARGES_KEY = "incomeFromRecurringCharges";
export const COST_PRICE = "costPrice";
export const COST_OF_SALES = "costOfSales";
export const RECURRING_CHARGES_INSTANCES_KEY = "recurringChargesInstances";
export const RECURRING_CHARGES_TOTAL_INCOME_KEY = "recurringChargesTotalIncome";
export const TOTAL_COST_OF_RECURRING_CHARGES_KEY =
  "totalCostOfRecurringCharges";

export const recurringChargesInstanceItems = [
  CUSTOMERS_AT_THE_START_KEY,
  SIGNUPS_KEY,
  RENEWABLES_KEY,
  CHURN_RATE_KEY,
  CUSTOMERS_AT_THE_END_KEY,
  RECURRING_CHARGE_KEY,
  INCOME_FROM_RECURRING_CHARGES_KEY,
  COST_PRICE,
  COST_OF_SALES,
];

export const recurringChargesTotalIncomeItems = [
  RECURRING_CHARGES_TOTAL_INCOME_KEY,
  TOTAL_COST_OF_RECURRING_CHARGES_KEY,
];

const shouldUseMonthly = (getReducerFunc) => {
  if (!getReducerFunc) {
    return false;
  }
  try {
    const forecastState = getReducerFunc(FINANCIAL_FORECAST_REDUCER_KEY);
    return (
      forecastState?.timeSeriesAssumptions?.primaryPeriodicity === "Monthly"
    );
  } catch (err) {
    return false;
  }
};

const getPeriodValue = (item, key, getReducerFunc) => {
  if (!item) {
    return 0;
  }
  if (shouldUseMonthly(getReducerFunc)) {
    const monthKey = item.months?.[key] ?? item.months?.[String(key)];
    if (monthKey !== undefined) {
      return Number(monthKey) || 0;
    }
  }
  return Number(item.years?.[key] || 0);
};

export const recurringChargesInstanceState = {
  [CUSTOMERS_AT_THE_START_KEY]: {
    title: "Customers at the Start",
    unit: unitType.NUMBER,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [GeneralCoType],
    allowDecimals: false,
    stripLeadingZeros: true,
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      if (yearKey === years.YEAR_1_KEY) {
        return 0;
      }

      // customers at the end of previous year
      const recurringChargesState = getReducerFunc(
        RECURRING_CHARGES_REDUCER_KEY
      );
      const instance =
        recurringChargesState[RECURRING_CHARGES_INSTANCES_KEY][instanceId];

      const customersAtTheEnd = Number(
        instance[CUSTOMERS_AT_THE_END_KEY]?.calculate(
          yearKey - 1,
          getReducerFunc,
          companyType,
          instanceId
        ) || 0
      );

      return customersAtTheEnd;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [SIGNUPS_KEY]: {
    title: "Signups",
    unit: unitType.NUMBER,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [GeneralCoType],
    allowDecimals: false,
    stripLeadingZeros: true,
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [CHURN_RATE_KEY]: {
    title: "Churn Rate",
    unit: unitType.PERCENTAGE,
    unitSymbol: getUnitSymbolByUnit(unitType.PERCENTAGE),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [GeneralCoType],
    stripLeadingZeros: true,
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [RENEWABLES_KEY]: {
    title: "Renewables",
    unit: unitType.NUMBER,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      if (!instanceId) {
        return 0;
      }

      // (customers at the start) * (minus (churn rate))
      const recurringChargesState = getReducerFunc(
        RECURRING_CHARGES_REDUCER_KEY
      );
      const instance =
        recurringChargesState[RECURRING_CHARGES_INSTANCES_KEY][instanceId];

      const customersAtTheStart = getPeriodValue(
        instance[CUSTOMERS_AT_THE_START_KEY],
        yearKey,
        getReducerFunc
      );
      const churnRate = getPeriodValue(
        instance[CHURN_RATE_KEY],
        yearKey,
        getReducerFunc
      );
      return customersAtTheStart * (1 - churnRate / 100);
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [CUSTOMERS_AT_THE_END_KEY]: {
    title: "Customers at the End",
    unit: unitType.NUMBER,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      if (!instanceId) {
        return 0;
      }

      // signups of previous year + renewables of previous year
      const recurringChargesState = getReducerFunc(
        RECURRING_CHARGES_REDUCER_KEY
      );
      const instance =
        recurringChargesState[RECURRING_CHARGES_INSTANCES_KEY][instanceId];

      const signups = getPeriodValue(
        instance[SIGNUPS_KEY],
        yearKey,
        getReducerFunc
      );

      const renewables = Number(
        instance[RENEWABLES_KEY]?.calculate(
          yearKey,
          getReducerFunc,
          companyType,
          instanceId
        ) || 0
      );

      return signups + renewables;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [RECURRING_CHARGE_KEY]: {
    title: "Recurring Charge",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [INCOME_FROM_RECURRING_CHARGES_KEY]: {
    title: "Income from Recurring Charges",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      if (!instanceId) {
        return 0;
      }

      // (customers at the start + signups) * (recurring charge)
      const recurringChargesState = getReducerFunc(
        RECURRING_CHARGES_REDUCER_KEY
      );
      const instance =
        recurringChargesState[RECURRING_CHARGES_INSTANCES_KEY][instanceId];
      const customersAtTheEnd = Number(
        instance[CUSTOMERS_AT_THE_END_KEY]?.calculate(
          yearKey,
          getReducerFunc,
          companyType,
          instanceId
        ) || 0
      );
      const recurringCharge = getPeriodValue(
        instance[RECURRING_CHARGE_KEY],
        yearKey,
        getReducerFunc
      );
      return customersAtTheEnd * recurringCharge;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [COST_PRICE]: {
    title: "Cost Price",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [COST_OF_SALES]: {
    title: "Cost of Sales",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      if (!instanceId) {
        return 0;
      }

      const instance = getReducerFunc(RECURRING_CHARGES_REDUCER_KEY)[
        RECURRING_CHARGES_INSTANCES_KEY
      ][instanceId];
      const costPrice = getPeriodValue(
        instance[COST_PRICE],
        yearKey,
        getReducerFunc
      );
      const customersAtTheEnd = Number(
        instance[CUSTOMERS_AT_THE_END_KEY]?.calculate(
          yearKey,
          getReducerFunc,
          companyType,
          instanceId
        ) || 0
      );
      return costPrice * customersAtTheEnd;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
};

export const initialState = {
  [RECURRING_CHARGES_INSTANCES_KEY]: {},
  [RECURRING_CHARGES_TOTAL_INCOME_KEY]: {
    title: "Income from Recurring Charges",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const recurringChargesState = getReducerFunc(
        RECURRING_CHARGES_REDUCER_KEY
      );
      const instances = recurringChargesState[RECURRING_CHARGES_INSTANCES_KEY];

      let total = 0;
      Object.entries(instances).forEach(([instanceId, instanceData]) => {
        total += Number(
          instanceData[INCOME_FROM_RECURRING_CHARGES_KEY]?.calculate(
            yearKey,
            getReducerFunc,
            companyType,
            instanceId
          ) || 0
        );
      });

      return total;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [TOTAL_COST_OF_RECURRING_CHARGES_KEY]: {
    title: "Cost of Recurring Charges",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const recurringChargesState = getReducerFunc(
        RECURRING_CHARGES_REDUCER_KEY
      );
      const instances = recurringChargesState[RECURRING_CHARGES_INSTANCES_KEY];

      let total = 0;
      Object.entries(instances).forEach(([instanceId, instanceData]) => {
        total += Number(
          instanceData[COST_OF_SALES]?.calculate(
            yearKey,
            getReducerFunc,
            companyType,
            instanceId
          ) || 0
        );
      });

      return total;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
};

export const defaultRecurringChargesState = {
  ...recurringChargesInstanceState,
};

const recurringChargesReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.ADD_RECURRING_CHARGES_INSTANCE:
      return {
        ...state,
        [RECURRING_CHARGES_INSTANCES_KEY]: {
          ...state[RECURRING_CHARGES_INSTANCES_KEY],
          [action.payload.id]: { ...defaultRecurringChargesState },
        },
      };
    case actionTypes.UPDATE_RECURRING_CHARGES_INSTANCE:
      return {
        ...state,
        [RECURRING_CHARGES_INSTANCES_KEY]: {
          ...state[RECURRING_CHARGES_INSTANCES_KEY],
          [action.payload.id]: {
            ...state[RECURRING_CHARGES_INSTANCES_KEY][action.payload.id],
            ...action.payload.data,
          },
        },
      };
    case actionTypes.REMOVE_RECURRING_CHARGES_INSTANCE: {
      const { [action.payload.id]: removed, ...remaining } =
        state[RECURRING_CHARGES_INSTANCES_KEY];
      return {
        ...state,
        [RECURRING_CHARGES_INSTANCES_KEY]: remaining,
      };
    }
    case actionTypes.SET_RECURRING_CHARGES_INSTANCE_BY_MONTH: {
      const instance = state[RECURRING_CHARGES_INSTANCES_KEY][action.id];
      const item = instance[action.itemKey];
      return {
        ...state,
        [RECURRING_CHARGES_INSTANCES_KEY]: {
          ...state[RECURRING_CHARGES_INSTANCES_KEY],
          [action.id]: {
            ...instance,
            [action.itemKey]: {
              ...item,
              months: {
                ...item.months,
                [action.monthKey]: action.amount,
              },
            },
          },
        },
      };
    }
    case actionTypes.REPLACE_RECURRING_CHARGES_INSTANCE_MONTHS: {
      const instance = state[RECURRING_CHARGES_INSTANCES_KEY][action.id];
      return {
        ...state,
        [RECURRING_CHARGES_INSTANCES_KEY]: {
          ...state[RECURRING_CHARGES_INSTANCES_KEY],
          [action.id]: {
            ...instance,
            [action.itemKey]: {
              ...instance[action.itemKey],
              months: action.payload,
            },
          },
        },
      };
    }
    case actionTypes.RESET_RECURRING_CHARGES_REDUCER:
      return initialState;
    default:
      return state;
  }
};

export default recurringChargesReducer;
